import { MpqArchive } from './mpq/archive';
import { normalizePath } from './mpq/crypto';
import type { PatchArchive, PatchHeader } from './patch';
import { matchesBase } from './patch';

/**
 * Layered read-only file system over several MPQ archives. Earlier archives win, the way the
 * game layers patch → expansion → base data.
 *
 * An optional patch layer (a BNUpdate patch such as `LOD_109D.EXE` or `D2Patch_114d.exe`) sits on
 * top: a file it patches is produced in memory the first time it is read — the full new file, or
 * a delta applied to the archive copy it was made against — and cached like any other read.
 * Nothing is written anywhere.
 */
export class ArchiveSet {
  private readonly cache = new Map<string, Promise<Uint8Array | null>>();
  private cacheBytes = 0;
  /** Files the patch layer could not patch (e.g. a different disc version), served unpatched. */
  readonly patchFailures = new Map<string, string>();

  /**
   * The game version the data is read as: the patch's (e.g. "1.09d"), else null (disc version).
   * `mountArchives` sets it when an installed patch archive (`patch_d2.mpq`) is the top layer.
   */
  version: string | null;

  constructor(
    readonly archives: MpqArchive[],
    readonly patch: PatchArchive | null = null,
    private readonly cacheLimit = 64 << 20,
  ) {
    this.version = patch?.version ?? null;
  }

  /** Which archive holds the unpatched `path` (useful for diagnostics). */
  source(path: string): MpqArchive | null {
    for (const a of this.archives) if (a.has(path)) return a;
    return null;
  }

  /** True if the patch layer serves `path`. */
  patched(path: string): boolean {
    return !!this.patch?.has(path) && !this.patchFailures.has(normalizePath(path));
  }

  has(path: string): boolean {
    return this.source(path) !== null || !!this.patch?.has(path);
  }

  /**
   * Size of the file `path` as it would be read, or null if absent. Unpatched files are answered
   * from the archive's block table without reading them; patched files are produced.
   */
  async size(path: string): Promise<number | null> {
    if (this.patched(path)) return (await this.tryRead(path))?.length ?? null;
    for (const a of this.archives) {
      const b = a.findBlock(path);
      if (b) return b.size;
    }
    return null;
  }

  tryRead(path: string): Promise<Uint8Array | null> {
    const key = normalizePath(path);
    const hit = this.cache.get(key);
    if (hit) {
      // Refresh LRU position.
      this.cache.delete(key);
      this.cache.set(key, hit);
      return hit;
    }
    const p = this.load(path, key);
    this.cache.set(key, p);
    p.then((d) => {
      if (!d) return;
      this.cacheBytes += d.length;
      this.evict();
    }).catch(() => this.cache.delete(key));
    return p;
  }

  async read(path: string): Promise<Uint8Array> {
    const d = await this.tryRead(path);
    if (!d) throw new Error(`file not found: ${path}`);
    return d;
  }

  async text(path: string): Promise<string> {
    const d = await this.read(path);
    let s = '';
    for (let i = 0; i < d.length; i += 0x8000) s += String.fromCharCode(...d.subarray(i, i + 0x8000));
    return s;
  }

  /** Union of every archive's listfile and the patch's files, de-duplicated case-insensitively. */
  async list(): Promise<string[]> {
    const seen = new Map<string, string>();
    for (const a of this.archives) for (const n of await a.list()) if (!seen.has(n.toUpperCase())) seen.set(n.toUpperCase(), n);
    for (const n of this.patch?.paths() ?? []) if (!seen.has(n.toUpperCase())) seen.set(n.toUpperCase(), n);
    return [...seen.values()];
  }

  /** Drops cached file contents (e.g. on memory pressure). */
  clearCache(): void {
    this.cache.clear();
    this.cacheBytes = 0;
  }

  private async load(path: string, key: string): Promise<Uint8Array | null> {
    if (this.patch?.has(path) && !this.patchFailures.has(key)) {
      try {
        const d = await this.patch.read(path, (p, h) => this.deltaBase(p, h));
        if (d) return d;
      } catch (e) {
        // Serve the disc's version rather than nothing; keep the reason for diagnostics.
        this.patchFailures.set(key, e instanceof Error ? e.message : String(e));
      }
    }
    return this.readUnpatched(path);
  }

  private async readUnpatched(path: string): Promise<Uint8Array | null> {
    const a = this.source(path);
    return a ? a.read(path) : null;
  }

  /**
   * The copy of `path` a delta was made against: the first archive whose copy has the delta's
   * size (block table) and CRC-32 — not necessarily the one that wins the layering (1.14d diffs
   * against the classic `d2data.mpq` copies `d2exp.mpq` shadows). Without a match, the top copy
   * (which the patch then rejects with a clear error); null if no archive holds the file.
   */
  async deltaBase(path: string, h: PatchHeader): Promise<Uint8Array | null> {
    for (const a of this.archives) {
      if (a.findBlock(path)?.size !== h.oldSize) continue;
      const d = await a.read(path);
      if (matchesBase(d, h)) return d;
    }
    return this.readUnpatched(path);
  }

  private evict(): void {
    if (this.cacheBytes <= this.cacheLimit) return;
    for (const [k, p] of this.cache) {
      if (this.cacheBytes <= this.cacheLimit * 0.75) break;
      this.cache.delete(k);
      p.then((d) => {
        if (d) this.cacheBytes -= d.length;
      });
    }
  }
}
